<script setup>
import {onMounted, ref} from 'vue';
import {_mft} from '../../i18n.js';
import {useNotificationStore} from '@/stores/NotificationStore.js';

const notificationStore = useNotificationStore();

const leads = ref([]);
const pagination = ref(null);
const canRestore = ref(false);
const search = ref('');
const loading = ref(false);
const loadError = ref(false);
const restoringIds = ref([]);

const loadLeads = async (page = 1) => {
    loading.value = true;
    loadError.value = false;

    try {
        const res = await axios.get(route('api.crm.leads.archived'), {
            params: {search: search.value, page},
        });

        leads.value = res.data.leads;
        pagination.value = res.data.pagination;
        canRestore.value = res.data.can_restore === true;
    } catch (error) {
        loadError.value = true;
    } finally {
        loading.value = false;
    }
};

const restoreLead = async (lead) => {
    if (restoringIds.value.includes(lead.id)) {
        return;
    }

    restoringIds.value.push(lead.id);

    try {
        const res = await axios.post(route('api.crm.leads.unarchive'), {lead_ids: [lead.id]});

        notificationStore.addNotification({
            type: 'success',
            title: _mft('crm:archive.result.restored', {count: res.data.succeeded}),
            dismissAfter: 5000,
        });
        await loadLeads(pagination.value?.current_page ?? 1);
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('crm:archive.error'),
            dismissAfter: 5000,
        });
    } finally {
        restoringIds.value = restoringIds.value.filter((id) => id !== lead.id);
    }
};

onMounted(() => {
    loadLeads();
});
</script>

<template>
    <div class="px-4 mx-auto mt-8 max-w-7xl sm:px-6 lg:px-8">
        <div class="w-full relative">
            <input-text
                type="text"
                :placeholder="_mft('crm:archive.list.searchPlaceholder')"
                v-model:content="search"
                @keydown.enter="loadLeads(1)"
                class="w-full"
            />
            <div class="flex items-center absolute top-1 right-0 bottom-0 pr-1">
                <button-base
                    style-type="primary"
                    size="input-embedded"
                    @click="loadLeads(1)"
                >
                    {{ _mft('crm:filters.search') }}
                </button-base>
            </div>
        </div>

        <p
            v-if="! canRestore && leads.length > 0"
            class="mt-2 text-xs text-gray-500"
        >
            {{ _mft('crm:archive.list.adminOnlyRestore') }}
        </p>

        <div class="mt-4 bg-white border rounded-xl">
            <div
                v-if="loading"
                class="flex justify-center py-8"
            >
                <mf-spinner-medium class="w-8 h-4" />
            </div>
            <p
                v-else-if="loadError"
                class="py-8 mb-0 text-sm text-center text-gray-500"
            >
                {{ _mft('crm:archive.error') }}
            </p>
            <p
                v-else-if="leads.length === 0"
                class="py-8 mb-0 text-sm text-center text-gray-500"
            >
                {{ _mft('crm:archive.list.empty') }}
            </p>
            <table
                v-else
                class="w-full text-left"
            >
                <tbody class="divide-y">
                    <tr
                        v-for="lead in leads"
                        :key="lead.id"
                    >
                        <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                            <a
                                :href="route('crm.leads.view', lead.id)"
                                class="mf-link"
                            >
                                {{ lead.name }}
                            </a><br>
                            <small>{{ lead.email }}</small>
                        </td>
                        <td class="hidden px-3 py-4 text-sm text-gray-700 md:table-cell">
                            {{ lead.title }}<br>
                            <small>{{ lead.company }}</small>
                        </td>
                        <td class="hidden px-3 py-4 text-xs text-gray-500 lg:table-cell">
                            <span v-if="lead.archived_by_name">
                                {{ _mft('crm:archive.list.archivedBy', {date: lead.archived_at_tz, name: lead.archived_by_name}) }}
                            </span>
                            <span v-else>
                                {{ _mft('crm:archive.list.archivedAt', {date: lead.archived_at_tz}) }}
                            </span>
                        </td>
                        <td class="py-4 pl-3 pr-4 text-right">
                            <button-secondary
                                v-if="canRestore"
                                size="sm"
                                :disabled="restoringIds.includes(lead.id)"
                                @click="restoreLead(lead)"
                            >
                                {{ _mft('crm:archive.restore') }}
                            </button-secondary>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <nav
            v-if="pagination && pagination.last_page > 1"
            class="flex items-center justify-center mt-6 space-x-2"
        >
            <button-secondary
                size="sm"
                :disabled="pagination.current_page <= 1"
                @click="loadLeads(pagination.current_page - 1)"
            >
                <i class="fa-light fa-chevron-left"></i>
            </button-secondary>
            <span class="text-sm text-gray-600">
                {{ pagination.current_page }} / {{ pagination.last_page }}
            </span>
            <button-secondary
                size="sm"
                :disabled="pagination.current_page >= pagination.last_page"
                @click="loadLeads(pagination.current_page + 1)"
            >
                <i class="fa-light fa-chevron-right"></i>
            </button-secondary>
        </nav>
    </div>
</template>

<style scoped>
</style>
